import { Injectable } from '@angular/core';
import { gsap } from 'gsap';

export type SlideDirection = 'left' | 'right' | 'top' | 'bottom';
export type EaseType = string;

export interface AnimationConfig {
  duration?: number;
  delay?: number;
  ease?: EaseType;
  onComplete?: () => void;
  bounce?: boolean;
}

export interface SwingConfig {
  rotation?: number;
  x?: number;
  duration?: number;
  origin?: string;
}

export interface WaveConfig {
  rotation?: number;
  duration?: number;
  scale?: number;
  repeat?: number;
}

export interface SequenceConfig {
  duration?: number;
  stagger?: number;
  ease?: EaseType;
  from?: gsap.TweenVars;
  to?: gsap.TweenVars;
  onComplete?: () => void;
}

export interface LockedInfoConfig {
  rotation?: number;
  scale?: number;
  waveDuration?: number;
  fadeDuration?: number;
  hold?: number;
}

@Injectable({
  providedIn: 'root',
})
export class GsapService {
  private readonly DEFAULT_DURATION = 0.5;
  private readonly DEFAULT_EASE = 'power1.out';

  set(element: HTMLElement, props: gsap.TweenVars): void {
    gsap.set(element, props);
  }

  to(element: HTMLElement, props: gsap.TweenVars, config: AnimationConfig = {}): void {
    gsap.killTweensOf(element);
    gsap.to(element, this.buildVars(props, config));
  }

  from(element: HTMLElement, props: gsap.TweenVars, config: AnimationConfig = {}): void {
    gsap.killTweensOf(element);
    gsap.from(element, this.buildVars(props, config));
  }

  fromTo(element: HTMLElement, fromProps: gsap.TweenVars, toProps: gsap.TweenVars, config: AnimationConfig = {}): void {
    gsap.killTweensOf(element);
    gsap.fromTo(element, fromProps, this.buildVars(toProps, config));
  }

  kill(element: HTMLElement): void {
    gsap.killTweensOf(element);
  }

  slideIn(element: HTMLElement, direction: SlideDirection, distance: number = 100, config: AnimationConfig = {}): void {
    const { duration = this.DEFAULT_DURATION, delay = 0, onComplete, bounce = false } = config;
    const offset = this.getSlideOffset(direction, distance);
    const ease = bounce ? 'elastic.out(0.8, 0.4)' : 'power2.out';

    this.fromTo(
      element,
      { x: offset.x, y: offset.y, opacity: 0 },
      { x: 0, y: 0, opacity: 1 },
      { duration, delay, ease, onComplete }
    );
  }

  slideOut(
    element: HTMLElement,
    direction: SlideDirection,
    distance: number = 100,
    config: AnimationConfig = {}
  ): void {
    const { duration = this.DEFAULT_DURATION, delay = 0, onComplete } = config;
    const offset = this.getSlideOffset(direction, distance);

    this.to(element, { x: offset.x, y: offset.y, opacity: 0 }, { duration, delay, ease: 'power2.in', onComplete });
  }

  fadeIn(element: HTMLElement, config: AnimationConfig = {}): void {
    const { duration = this.DEFAULT_DURATION, delay = 0, ease = this.DEFAULT_EASE, onComplete } = config;
    this.fromTo(element, { opacity: 0 }, { opacity: 1 }, { duration, delay, ease, onComplete });
  }

  fadeOut(element: HTMLElement, config: AnimationConfig = {}): void {
    const { duration = this.DEFAULT_DURATION, delay = 0, ease = this.DEFAULT_EASE, onComplete } = config;
    this.to(element, { opacity: 0 }, { duration, delay, ease, onComplete });
  }

  scaleIn(element: HTMLElement, config: AnimationConfig = {}): void {
    const { duration = this.DEFAULT_DURATION, delay = 0, ease = 'back.out(1.7)', onComplete } = config;
    this.fromTo(element, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1 }, { duration, delay, ease, onComplete });
  }

  scaleOut(element: HTMLElement, config: AnimationConfig = {}): void {
    const { duration = this.DEFAULT_DURATION, delay = 0, ease = 'back.in(1.2)', onComplete } = config;
    this.to(element, { scale: 0, opacity: 0 }, { duration, delay, ease, onComplete });
  }

  scaleInOut(element: HTMLElement, config: AnimationConfig & { holdIn?: number; holdOut?: number } = {}): void {
    const {
      duration = this.DEFAULT_DURATION,
      delay = 0,
      ease = 'power1.inOut',
      holdIn = 0.3,
      holdOut = holdIn,
    } = config;

    this.kill(element);

    const tl = gsap.timeline({ repeat: -1, delay });
    tl.fromTo(element, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration, ease })
      .to(element, { duration: holdIn })
      .to(element, { scale: 0, opacity: 0, duration, ease })
      .to(element, { duration: holdOut });
  }

  popIn(element: HTMLElement, config: AnimationConfig = {}): void {
    const { delay = 0, onComplete } = config;
    this.fromTo(
      element,
      { scale: 0, opacity: 0 },
      { scale: 1, opacity: 1 },
      { duration: 0.6, delay, ease: 'elastic.out(1, 0.5)', onComplete }
    );
  }

  shrinkOut(element: HTMLElement, config: AnimationConfig = {}): void {
    const { onComplete } = config;
    this.to(element, { scale: 0, opacity: 0 }, { duration: 0.25, ease: 'back.in(1.2)', onComplete });
  }

  zoomIn(element: HTMLElement, config: AnimationConfig = {}): void {
    const { delay = 0, onComplete } = config;
    this.fromTo(
      element,
      { scale: 5, opacity: 0, z: 100 },
      { scale: 1, opacity: 1, z: 0 },
      { duration: 0.7, delay, ease: 'expo.out', onComplete }
    );
  }

  zoomOut(element: HTMLElement, config: AnimationConfig = {}): void {
    const { delay = 0, onComplete } = config;
    this.to(
      element,
      { scale: 5, opacity: 0, z: 100 },
      {
        duration: 0.6,
        delay,
        ease: 'expo.in',
        onComplete: () => {
          this.set(element, { display: 'none' });
          if (onComplete) onComplete();
        },
      }
    );
  }

  swing(element: HTMLElement, config: SwingConfig = {}): void {
    const { rotation = 5, x = 15, duration = 2, origin = 'top center' } = config;

    this.kill(element);

    const isHorizontal = rotation === 0;

    if (!isHorizontal) {
      gsap.set(element, { transformOrigin: origin });
    }

    const halfDuration = duration / 2;
    const ease = 'sine.inOut';

    if (isHorizontal) {
      gsap.to(element, {
        x: -x,
        duration: halfDuration,
        ease,
        onComplete: () => gsap.to(element, { x, duration, ease, yoyo: true, repeat: -1 }),
      });
    } else {
      gsap.to(element, {
        rotation: -rotation,
        duration: halfDuration,
        ease,
        onComplete: () => gsap.to(element, { rotation, duration, ease, yoyo: true, repeat: -1 }),
      });
    }
  }

  sway(element: HTMLElement, config: { y?: number; duration?: number; reverse?: boolean } = {}): void {
    const { y = 5, duration = 1, reverse = false } = config;
    const startY = this.vh(reverse ? y : -y);
    const endY = this.vh(reverse ? -y : y);

    this.kill(element);

    gsap.set(element, { y: startY });
    gsap.to(element, {
      y: endY,
      duration: duration / 2,
      ease: 'sine.inOut',
      onComplete: () => gsap.to(element, { y: startY, duration, ease: 'sine.inOut', yoyo: true, repeat: -1 }),
    });
  }

  stopSwing(element: HTMLElement): void {
    this.kill(element);
    this.set(element, { rotation: 0, x: 0 });
  }

  lockedInfoSequence(glyph: HTMLElement, info: HTMLElement, config: LockedInfoConfig = {}): void {
    const { rotation = 60, scale = 0.7, waveDuration = 0.65, fadeDuration = 0.4, hold = 1.2 } = config;
    const half = waveDuration / 2;

    this.kill(glyph);
    this.kill(info);

    gsap.set(glyph, { transformOrigin: 'bottom center' });
    gsap.set(info, { opacity: 0 });

    gsap
      .timeline()
      .to(glyph, { rotation: -rotation, scale, y: this.vh(-5), x: this.vh(2.5), duration: half, ease: 'sine.inOut' })
      .to(glyph, { rotation: 0, scale: 1, y: 0, x: 0, duration: half, ease: 'sine.in' })
      .to(glyph, { opacity: 0, duration: fadeDuration, ease: this.DEFAULT_EASE })
      .to(info, { opacity: 1, duration: fadeDuration, ease: this.DEFAULT_EASE })
      .to(info, { duration: hold })
      .to(info, { opacity: 0, duration: fadeDuration, ease: this.DEFAULT_EASE })
      .to(glyph, { opacity: 1, duration: fadeDuration, ease: this.DEFAULT_EASE });
  }

  pressDown(
    element: HTMLElement,
    config: { scale?: number; brightness?: number; duration?: number; onComplete?: () => void } = {}
  ): void {
    const { scale = 0.95, brightness = 0.95, duration = 0.2, onComplete } = config;
    const half = duration / 2;

    gsap.killTweensOf(element);
    gsap
      .timeline({ onComplete })
      .to(element, { scale, filter: `brightness(${brightness})`, duration: half })
      .to(element, { scale: 1, duration: half });
  }

  sequence(elements: HTMLElement[], config: SequenceConfig = {}): void {
    const {
      duration = this.DEFAULT_DURATION,
      stagger = 0.1,
      ease = 'back.out(1.7)',
      from = { scale: 0, opacity: 0 },
      to = { scale: 1, opacity: 1 },
      onComplete,
    } = config;

    const timeline = gsap.timeline({ onComplete });
    timeline.set(elements, from);
    timeline.to(elements, { ...to, duration, ease, stagger });
  }

  createTimeline(config: { onComplete?: () => void } = {}): gsap.core.Timeline {
    return gsap.timeline({ onComplete: config.onComplete });
  }

  private buildVars(props: gsap.TweenVars, config: AnimationConfig): gsap.TweenVars {
    const { duration = this.DEFAULT_DURATION, delay = 0, ease = this.DEFAULT_EASE, onComplete } = config;
    return { ...props, duration, delay, ease, ...(onComplete && { onComplete }) };
  }

  private vh(value: number): number {
    return (value * window.innerHeight) / 100;
  }

  private vw(value: number): number {
    return (value * window.innerWidth) / 100;
  }

  private getSlideOffset(direction: SlideDirection, distance: number): { x: number; y: number } {
    const offsets = {
      left: { x: -distance, y: 0 },
      right: { x: distance, y: 0 },
      top: { x: 0, y: -distance },
      bottom: { x: 0, y: distance },
    };
    return offsets[direction];
  }

  floatingHorizontal(element: HTMLElement, distance = 50, duration = 5): gsap.core.Timeline {
    return this.createTimeline().fromTo(
      element,
      { x: -distance },
      {
        x: distance,
        duration,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      }
    );
  }
}
